// src/components/admin/payments/SendInvoiceModal.jsx
import { useState, useEffect } from 'react';
import {
  FaTimes,
  FaPlus,
  FaTrash,
  FaSpinner,
  FaPaperPlane,
  FaSave,
  FaUser,
  FaEnvelope,
} from 'react-icons/fa';
import useCustomers from '../../../hooks/useCustomers';

const EMPTY_LINE_ITEM = { description: '', amount: '', quantity: 1 };

// Convert a stored draft back into form-friendly values
const draftToFormData = (draft) => {
  // items are stored in cents, convert back to dollars for display
  const lineItems = draft.items?.map((item) => ({
    description: item.description || '',
    amount: ((item.amount || 0) / 100).toFixed(2),
    quantity: item.quantity || 1,
  })) || [{ ...EMPTY_LINE_ITEM }];

  // Rebuild dueDate string from daysUntilDue if no explicit date stored
  let dueDate = '';
  if (draft.dueDate) {
    const d = draft.dueDate instanceof Date ? draft.dueDate : new Date(draft.dueDate);
    dueDate = d.toISOString().split('T')[0];
  } else if (draft.daysUntilDue && draft.daysUntilDue !== 30) {
    const d = new Date();
    d.setDate(d.getDate() + draft.daysUntilDue);
    dueDate = d.toISOString().split('T')[0];
  }

  return {
    formData: {
      customerName: draft.customerName || '',
      customerEmail: draft.email || '',
      dueDate,
      memo: draft.memo || '',
    },
    lineItems,
  };
};

const SendInvoiceModal = ({
  isOpen,
  onClose,
  onInvoiceSent,
  onSaveDraft,
  onUpdateDraft,
  sending,
  editingDraft = null, // draft object when editing, null when creating new
}) => {
  const { customers, loading: customersLoading } = useCustomers();

  const [mode, setMode] = useState('list');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    dueDate: '',
    memo: '',
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE_ITEM }]);
  const [error, setError] = useState('');

  // ── Pre-populate when editing a draft ────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    if (editingDraft) {
      const { formData: fd, lineItems: li } = draftToFormData(editingDraft);
      setFormData(fd);
      setLineItems(li);
      // Default to manual mode when editing — the email is already known
      setMode('manual');
      setSelectedCustomerId('');
      setSearch('');
    } else {
      // Reset to blank for new invoice
      setFormData({ customerName: '', customerEmail: '', dueDate: '', memo: '' });
      setLineItems([{ ...EMPTY_LINE_ITEM }]);
      setMode('list');
      setSelectedCustomerId('');
      setSearch('');
    }
    setError('');
  }, [isOpen, editingDraft]);

  if (!isOpen) return null;

  const isEditing = !!editingDraft;

  // ── Customer list ─────────────────────────────────────────────────────────
  const filteredCustomers = customers.filter((c) => {
    const term = search.toLowerCase();
    return (
      `${c.firstName} ${c.lastName}`.toLowerCase().includes(term) ||
      c.email.toLowerCase().includes(term)
    );
  });

  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId);

  const handleModeSwitch = (newMode) => {
    setMode(newMode);
    setSelectedCustomerId('');
    setSearch('');
    setFormData((prev) => ({ ...prev, customerName: '', customerEmail: '' }));
    setError('');
  };

  // ── Form helpers ──────────────────────────────────────────────────────────
  const handleFormChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleLineItemChange = (index, field, value) =>
    setLineItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

  const addLineItem = () => setLineItems((prev) => [...prev, { ...EMPTY_LINE_ITEM }]);

  const removeLineItem = (index) => {
    if (lineItems.length === 1) return;
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const getTotal = () =>
    lineItems.reduce(
      (sum, item) =>
        sum + (parseFloat(item.amount) || 0) * (parseInt(item.quantity) || 1),
      0
    );

  // ── Build + validate payload ──────────────────────────────────────────────
  const buildPayload = () => {
    let customerName = '';
    let customerEmail = '';

    if (mode === 'list') {
      if (!selectedCustomer) { setError('Please select a customer'); return null; }
      customerName = `${selectedCustomer.firstName} ${selectedCustomer.lastName}`.trim();
      customerEmail = selectedCustomer.email;
    } else {
      customerName = formData.customerName.trim();
      customerEmail = formData.customerEmail.trim();
      if (!customerName) { setError('Customer name is required'); return null; }
      if (!customerEmail || !/\S+@\S+\.\S+/.test(customerEmail)) {
        setError('A valid email address is required');
        return null;
      }
    }

    for (const item of lineItems) {
      if (!item.description.trim()) { setError('Each line item needs a description'); return null; }
      if (!item.amount || parseFloat(item.amount) <= 0) {
        setError('Each line item needs an amount greater than $0');
        return null;
      }
    }

    return {
      customerName,
      customerEmail,
      lineItems: lineItems.map((item) => ({
        description: item.description.trim(),
        amount: parseFloat(item.amount),
        quantity: parseInt(item.quantity) || 1,
      })),
      dueDate: formData.dueDate || null,
      memo: formData.memo.trim() || null,
    };
  };

  const resetAndClose = () => {
    setError('');
    onClose();
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleSend = async () => {
    setError('');
    const payload = buildPayload();
    if (!payload) return;
    try {
      if (isEditing) {
        // Approve the draft directly: update then send
        await onUpdateDraft(editingDraft.id, payload);
        await onInvoiceSent({ ...payload, _fromDraftId: editingDraft.id });
      } else {
        await onInvoiceSent(payload);
      }
      resetAndClose();
    } catch (err) {
      setError(err.message || 'Failed to send invoice');
    }
  };

  const handleSaveDraft = async () => {
    setError('');
    const payload = buildPayload();
    if (!payload) return;
    try {
      if (isEditing) {
        await onUpdateDraft(editingDraft.id, payload);
      } else {
        await onSaveDraft(payload);
      }
      resetAndClose();
    } catch (err) {
      setError(err.message || 'Failed to save draft');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isEditing ? 'bg-blue-100' : 'bg-green-100'}`}>
              <FaPaperPlane className={`w-5 h-5 ${isEditing ? 'text-blue-600' : 'text-green-600'}`} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Edit Draft' : 'New Invoice'}
              </h2>
              <p className="text-sm text-gray-500">
                {isEditing
                  ? 'Update the draft then save or send it out'
                  : 'Send now or save as a draft for later'}
              </p>
            </div>
          </div>
          <button
            onClick={resetAndClose}
            disabled={sending}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Mode toggle — hidden when editing since email is already filled */}
          {!isEditing && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Send To
              </h3>
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
                <button
                  onClick={() => handleModeSwitch('list')}
                  className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    mode === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <FaUser className="w-3 h-3" /> Customer List
                </button>
                <button
                  onClick={() => handleModeSwitch('manual')}
                  className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    mode === 'manual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <FaEnvelope className="w-3 h-3" /> Email Address
                </button>
              </div>
            </div>
          )}

          {/* Customer picker — new invoice only */}
          {!isEditing && mode === 'list' && (
            <div>
              {customersLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-400 py-4">
                  <FaSpinner className="w-4 h-4 animate-spin" /> Loading customers...
                </div>
              ) : customers.length === 0 ? (
                <p className="text-sm text-gray-500 py-4">
                  No customers found. Add customers on the Customers page first, or switch to Email Address mode.
                </p>
              ) : (
                <>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search customers..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <div className="border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                    {filteredCustomers.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-6">No customers match</p>
                    ) : (
                      filteredCustomers.map((customer) => {
                        const isSelected = customer.id === selectedCustomerId;
                        return (
                          <button
                            key={customer.id}
                            onClick={() => { setSelectedCustomerId(customer.id); setError(''); }}
                            className={`w-full flex items-center justify-between px-4 py-3 text-left text-sm transition-colors border-b border-gray-100 last:border-0 ${
                              isSelected ? 'bg-green-50 text-green-800' : 'hover:bg-gray-50 text-gray-700'
                            }`}
                          >
                            <div>
                              <span className="font-medium">{customer.firstName} {customer.lastName}</span>
                              <span className="text-gray-400 ml-2">{customer.email}</span>
                            </div>
                            {isSelected && (
                              <span className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                                Selected
                              </span>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                  {selectedCustomer && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                      <FaUser className="w-3 h-3 flex-shrink-0" />
                      Invoice will be sent to{' '}
                      <strong className="mx-1">{selectedCustomer.firstName} {selectedCustomer.lastName}</strong>
                      at <strong className="ml-1">{selectedCustomer.email}</strong>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Manual / edit email fields */}
          {(isEditing || mode === 'manual') && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.customerName}
                  onChange={(e) => handleFormChange('customerName', e.target.value)}
                  placeholder="John Smith"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) => handleFormChange('customerEmail', e.target.value)}
                  placeholder="john@example.com"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>
          )}

          {/* Line Items */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Line Items
            </h3>
            <div className="hidden sm:flex gap-3 mb-1 text-xs text-gray-400">
              <div className="flex-1">Description</div>
              <div className="w-20 text-center">Qty</div>
              <div className="w-28 text-center">Unit Price</div>
              <div className="w-8" />
            </div>
            <div className="space-y-3">
              {lineItems.map((item, index) => (
                <div key={index} className="flex gap-3 items-center">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                      placeholder="e.g. Lawn mowing — May"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div className="w-20">
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                      min="1"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div className="w-28 relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <input
                      type="number"
                      value={item.amount}
                      onChange={(e) => handleLineItemChange(index, 'amount', e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg pl-6 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={() => removeLineItem(index)}
                    disabled={lineItems.length === 1}
                    className="p-2 text-gray-300 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={addLineItem}
              className="mt-3 flex items-center gap-2 text-sm text-green-600 hover:text-green-700 font-medium"
            >
              <FaPlus className="w-3 h-3" /> Add line item
            </button>
          </div>

          {/* Total */}
          <div className="bg-gray-50 rounded-lg p-4 flex justify-between items-center">
            <span className="font-semibold text-gray-700">Invoice Total</span>
            <span className="text-2xl font-bold text-gray-900">${getTotal().toFixed(2)}</span>
          </div>

          {/* Due Date & Memo */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date <span className="text-gray-400 font-normal">(optional, default 30 days)</span>
              </label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => handleFormChange('dueDate', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Memo <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={formData.memo}
                onChange={(e) => handleFormChange('memo', e.target.value)}
                placeholder="e.g. Thank you for your business!"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={resetAndClose}
            disabled={sending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <div className="flex gap-3">
            {/* Save / Update Draft */}
            <button
              onClick={handleSaveDraft}
              disabled={sending}
              className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {sending ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaSave className="w-4 h-4" />}
              {isEditing ? 'Update Draft' : 'Save as Draft'}
            </button>
            {/* Send */}
            <button
              onClick={handleSend}
              disabled={sending}
              className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {sending ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaPaperPlane className="w-4 h-4" />}
              {isEditing ? 'Update & Send' : 'Send Invoice'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendInvoiceModal;